"use client";

import { getCurrentMaterial } from "@tosize/tosize-furniture-configurator/src/components/renderer/data";
import { formatNumber } from "@tosize/ui";
import { P, match } from "ts-pattern";
import type { LangParams } from "~/app/[lang]/layout";
import { useCabinet } from "./Context";

type Variant =
	| "width"
	| "height"
	| "depth"
	| "material"
	| "open"
	| "doors"
	| "shelves"
	| "legs"
	| "back";

interface Props extends LangParams {
	defaultTitle: string;
	emptyTitle: string;
	fullTitle: string;
	options: Record<Variant, string>;
}

export function SpecsContent({ lang, defaultTitle, emptyTitle, fullTitle, options }: Props) {
	const { store, api } = useCabinet();

	if (!store) return null;

	const material = getCurrentMaterial(store, api);
	const doors = Array.from(store.compartments.values())
		.filter((c) => !!c.door)
		.reduce((a, b) => a + (b.door?.type === "double" ? 2 : 1), 0);
	const shelves = Array.from(store.compartments.values()).reduce((a, b) => a + b.shelves.length, 0);
	const open = store.compartments.size - doors;

	function Item(props: { title: string; value?: string | number | boolean }) {
		return (
			<div>
				<b>{props.title}</b>
				<br />
				<span>
					{match(props.value)
						.with(P.number, (value) => (value > 0 ? value : emptyTitle))
						.with(P.string, (value) => value ?? emptyTitle)
						.with(P.boolean, (value) => (value ? fullTitle : emptyTitle))
						.otherwise(() => defaultTitle)}
				</span>
			</div>
		);
	}

	return (
		<div className="prose grid max-w-none grid-cols-2 gap-x-4 gap-y-2 text-shades-dark sm:grid-cols-3 md:grid-cols-4">
			<Item
				title={options.width}
				value={formatNumber({ lang, value: store.width / 10, unit: "centimeter" })}
			/>
			<Item
				title={options.height}
				value={formatNumber({ lang, value: store.height / 10, unit: "centimeter" })}
			/>
			<Item
				title={options.depth}
				value={formatNumber({ lang, value: store.depth / 10, unit: "centimeter" })}
			/>

			{!!material?.name && (
				<Item
					title={options.material}
					value={`${material.name} ${formatNumber({
						lang,
						value: store.thickness,
						unit: "millimeter",
						digits: { min: 0, max: 0 },
					})}`}
				/>
			)}

			<span className="col-span-full" />

			<Item title={options.open} value={open} />
			<Item title={options.doors} value={doors} />
			<Item title={options.shelves} value={shelves} />

			<span className="col-span-full" />

			<Item title={options.legs} value={store.hasFeet} />
			<Item title={options.back} />
		</div>
	);
}
